import { getParent, getRoot, types } from 'mobx-state-tree'
import { GameChoice } from '../game/GameChoice'

/**
 * A single survey question in the play flow. Deliberately mirrors the read/action
 * surface of `GameQuestion` that the shared quiz components consume (`MC`/`MCA`/`TF`,
 * `selected`, `answered`, `answer()`, etc.) so it can be rendered through the same
 * `QuizItemList` / `Question` pipeline. Surveys are ungraded, so all correctness /
 * feedback views are inert.
 */
export const SurveyPlayQuestion = types
  .model('SurveyPlayQuestion')
  .props({
    id: types.identifierNumber,
    text: types.string,
    image_url: types.maybe(types.string),
    // Normalized game vocabulary, used by the shared render switch.
    question_type: types.enumeration([
      'Multiple Choice',
      'Multiple Response',
      'True or False',
      'Open Text',
      'Scale',
    ]),
    // Original snake_case type, required by the submit payload.
    survey_type: types.string,
    choices: types.array(GameChoice),
    min_label: types.maybeNull(types.string),
    max_label: types.maybeNull(types.string),
    answered: types.maybe(types.boolean),
    userText: types.maybe(types.string),
    rangeValue: types.maybe(types.number),
  })
  .actions((self) => ({
    setUserText(text: string) {
      self.userText = text
    },
    setRangeValue(value: number) {
      self.rangeValue = value
    },
    // No-op: surveys never show answer feedback. Kept so the shared components
    // that reference this action on a question don't break.
    setShowFeedback() {},
  }))
  .views((self) => ({
    get TF() {
      return self.question_type === 'True or False'
    },
    get MC() {
      return self.question_type === 'Multiple Choice'
    },
    get MCA() {
      return self.question_type === 'Multiple Response'
    },
    get selected() {
      return self.choices.filter((c) => c.selected)
    },
    // Surveys are ungraded — no correctness or feedback.
    get answeredCorrectly() {
      return false
    },
    get showFeedback() {
      return false
    },
    get showChoiceFeedback() {
      return false
    },
    get correctChoices() {
      return [] as any[]
    },
    get longChoices() {
      return (
        self.choices?.length === 4 ||
        self.choices?.some((choice: any) => choice?.text?.length >= 100)
      )
    },
    get longQuestion() {
      return self.text?.length >= 400
    },
    get rootStore(): any {
      return getRoot(self)
    },
  }))
  .actions((self) => ({
    next(fromFactoid?: boolean) {
      const survey = getParent(self, 2) as any
      if (survey) {
        survey.next(fromFactoid)
      }
    },
    continueFromFeedback() {
      this.next(true)
    },
    answer() {
      self.answered = true
      this.next(false)
    },
  }))
